import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Slider,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as ResetIcon,
  Download as ExportIcon,
  Upload as ImportIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Save as SaveIcon
} from '@mui/icons-material';

import { useTheme } from '../../contexts/ThemeContext';
import { useDashboard } from '../../hooks/useDashboard';
import { DashboardLayout } from '../../types';
import ColorPicker from './ColorPicker';
import ThemePresets from './ThemePresets';

interface ThemeCustomizerProps {
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`theme-tabpanel-${index}`}
      aria-labelledby={`theme-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
};

const ThemeCustomizer: React.FC<ThemeCustomizerProps> = ({ open, onClose }) => {
  const {
    currentTheme,
    isDarkMode,
    updateTheme,
    toggleDarkMode,
    resetTheme,
    exportTheme,
    importTheme
  } = useTheme();

  const { config, updateConfig } = useDashboard();
  const currentLayout = config?.layout;

  const [activeTab, setActiveTab] = useState(0);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [importError, setImportError] = useState('');
  const [localLayout, setLocalLayout] = useState<DashboardLayout | null>(null);
  const [layoutSaving, setLayoutSaving] = useState(false);
  const [layoutSaveSuccess, setLayoutSaveSuccess] = useState(false);
  const [localTheme, setLocalTheme] = useState<typeof currentTheme | null>(null);
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeSaveSuccess, setThemeSaveSuccess] = useState(false);

  // Initialize local layout state when config changes
  React.useEffect(() => {
    if (currentLayout) {
      setLocalLayout({
        // Provide defaults for all required properties
        headerTitle: currentLayout.headerTitle || '',
        headerIcon: currentLayout.headerIcon || 'dashboard',
        showSearch: currentLayout.showSearch ?? true,
        tilesPerRow: currentLayout.tilesPerRow || 4,
        groupSpacing: currentLayout.groupSpacing || 24,
        tileSpacing: currentLayout.tileSpacing || 16,
        // Include existing optional properties
        headerIconType: currentLayout.headerIconType,
        headerIconUrl: currentLayout.headerIconUrl,
        // Include new properties with defaults
        groupHorizontalPadding: currentLayout.groupHorizontalPadding || 16,
        groupHorizontalMargin: currentLayout.groupHorizontalMargin || 8,
        itemHeight: currentLayout.itemHeight || 120
      });
    }
  }, [currentLayout]);

  // Initialize local theme state when theme changes
  React.useEffect(() => {
    setLocalTheme({ ...currentTheme });
  }, [currentTheme]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleColorChange = (property: keyof typeof currentTheme) => (color: string) => {
    if (localTheme) {
      setLocalTheme({ ...localTheme, [property]: color });
    }
  };

  const handleCustomColorChange = (colorKey: 'accent1' | 'accent2') => (color: string) => {
    if (localTheme) {
      setLocalTheme({
        ...localTheme,
        customColors: {
          ...localTheme.customColors,
          [colorKey]: color
        }
      });
    }
  };

  const handleBorderRadiusChange = (event: Event, value: number | number[]) => {
    if (localTheme) {
      setLocalTheme({ ...localTheme, borderRadius: value as number });
    }
  };

  // Save theme changes to server
  const handleSaveTheme = async () => {
    if (!localTheme) return;
    
    try {
      setThemeSaving(true);
      await updateTheme(localTheme);
      setThemeSaveSuccess(true);
      setTimeout(() => setThemeSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save theme:', error);
    } finally {
      setThemeSaving(false);
    }
  };

  // Check if theme has unsaved changes
  const hasThemeChanges = localTheme && (
    localTheme.mode !== currentTheme.mode ||
    localTheme.primaryColor !== currentTheme.primaryColor ||
    localTheme.secondaryColor !== currentTheme.secondaryColor ||
    localTheme.backgroundColor !== currentTheme.backgroundColor ||
    localTheme.textColor !== currentTheme.textColor ||
    localTheme.cardBackground !== currentTheme.cardBackground ||
    localTheme.borderRadius !== currentTheme.borderRadius ||
    JSON.stringify(localTheme.customColors) !== JSON.stringify(currentTheme.customColors)
  );

  // Layout change handlers (update local state only)
  const handleLayoutChange = (property: keyof DashboardLayout) => (value: number | number[]) => {
    if (localLayout) {
      setLocalLayout({
        ...localLayout,
        [property]: value as number
      });
    }
  };

  // Save layout changes to server
  const handleSaveLayout = async () => {
    if (!localLayout || !config) return;
    
    try {
      setLayoutSaving(true);
      await updateConfig({
        ...config,
        layout: localLayout
      });
      setLayoutSaveSuccess(true);
      setTimeout(() => setLayoutSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save layout:', error);
    } finally {
      setLayoutSaving(false);
    }
  };

  // Check if layout has unsaved changes
  const hasLayoutChanges = localLayout && currentLayout && (
    localLayout.groupHorizontalPadding !== (currentLayout.groupHorizontalPadding || 16) ||
    localLayout.groupHorizontalMargin !== (currentLayout.groupHorizontalMargin || 8) ||
    localLayout.itemHeight !== (currentLayout.itemHeight || 120) ||
    localLayout.groupSpacing !== currentLayout.groupSpacing ||
    localLayout.tileSpacing !== currentLayout.tileSpacing
  );

  const handleExport = () => {
    const themeJson = exportTheme();
    const blob = new Blob([themeJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard-theme-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    setExportSuccess(true);
    setTimeout(() => setExportSuccess(false), 3000);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          const success = importTheme(content);
          if (!success) {
            setImportError('Invalid theme file format');
            setTimeout(() => setImportError(''), 5000);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset the theme to default? All customizations will be lost.')) {
      resetTheme();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', maxHeight: 800 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Theme Customizer
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Export theme">
            <IconButton onClick={handleExport} size="small">
              <ExportIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Import theme">
            <IconButton onClick={handleImport} size="small">
              <ImportIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset to default">
            <IconButton onClick={handleReset} size="small" color="error">
              <ResetIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Status Messages */}
        {exportSuccess && (
          <Alert severity="success" sx={{ m: 2, mb: 0 }}>
            Theme exported successfully!
          </Alert>
        )}
        {importError && (
          <Alert severity="error" sx={{ m: 2, mb: 0 }}>
            {importError}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="theme customizer tabs">
            <Tab label="Colors" />
            <Tab label="Layout" />
            <Tab label="Presets" />
          </Tabs>
        </Box>

        {/* Colors Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ px: 3 }}>
            <Stack spacing={3}>
              {/* Mode Toggle */}
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {localTheme?.mode === 'dark' ? <DarkModeIcon /> : <LightModeIcon />}
                      <Box>
                        <Typography variant="subtitle1">
                          {localTheme?.mode === 'dark' ? 'Dark Mode' : 'Light Mode'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Switch between light and dark themes
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={localTheme?.mode === 'dark'}
                      onChange={(event) => {
                        if (localTheme) {
                          const newMode = localTheme.mode === 'dark' ? 'light' : 'dark';
                          const modeUpdates = {
                            mode: newMode,
                            ...(newMode === 'dark' ? {
                              backgroundColor: '#121212',
                              cardBackground: '#1e1e1e', 
                              textColor: '#ffffff',
                              primaryColor: '#90caf9',
                              secondaryColor: '#f48fb1'
                            } : {
                              backgroundColor: '#f5f5f5',
                              cardBackground: '#ffffff',
                              textColor: '#333333', 
                              primaryColor: '#1976d2',
                              secondaryColor: '#dc004e'
                            })
                          };
                          setLocalTheme({ ...localTheme, ...modeUpdates } as typeof currentTheme);
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* Primary Colors */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Primary Colors
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <ColorPicker
                      label="Primary Color"
                      value={localTheme?.primaryColor || currentTheme.primaryColor}
                      onChange={handleColorChange('primaryColor')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ColorPicker
                      label="Secondary Color"
                      value={localTheme?.secondaryColor || currentTheme.secondaryColor}
                      onChange={handleColorChange('secondaryColor')}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Background Colors */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Background Colors
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <ColorPicker
                      label="Background Color"
                      value={localTheme?.backgroundColor || currentTheme.backgroundColor}
                      onChange={handleColorChange('backgroundColor')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ColorPicker
                      label="Card Background"
                      value={localTheme?.cardBackground || currentTheme.cardBackground}
                      onChange={handleColorChange('cardBackground')}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Text Colors */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Text Color
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <ColorPicker
                      label="Text Color"
                      value={localTheme?.textColor || currentTheme.textColor}
                      onChange={handleColorChange('textColor')}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Custom Colors */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Accent Colors
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <ColorPicker
                      label="Accent Color 1"
                      value={localTheme?.customColors.accent1 || currentTheme.customColors.accent1}
                      onChange={handleCustomColorChange('accent1')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ColorPicker
                      label="Accent Color 2"
                      value={localTheme?.customColors.accent2 || currentTheme.customColors.accent2}
                      onChange={handleCustomColorChange('accent2')}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Theme Save Controls */}
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
                {hasThemeChanges && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You have unsaved theme changes. Click "Save Theme Changes" to apply them.
                  </Alert>
                )}
                
                {themeSaveSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Theme changes saved successfully!
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveTheme}
                    disabled={!hasThemeChanges || themeSaving}
                    startIcon={<SaveIcon />}
                  >
                    {themeSaving ? 'Saving...' : 'Save Theme Changes'}
                  </Button>
                  
                  {hasThemeChanges && (
                    <Button
                      variant="outlined"
                      onClick={() => setLocalTheme({ ...currentTheme })}
                      disabled={themeSaving}
                      startIcon={<ResetIcon />}
                    >
                      Reset Changes
                    </Button>
                  )}
                </Box>
              </Box>
            </Stack>
          </Box>
        </TabPanel>

        {/* Layout Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ px: 3 }}>
            <Stack spacing={3}>
              {/* Border Radius */}
              <Box>
                <Typography gutterBottom>
                  Border Radius: {localTheme?.borderRadius || currentTheme.borderRadius}px
                </Typography>
                <Slider
                  value={localTheme?.borderRadius || currentTheme.borderRadius}
                  onChange={handleBorderRadiusChange}
                  min={0}
                  max={24}
                  step={1}
                  marks={[
                    { value: 0, label: '0px' },
                    { value: 8, label: '8px' },
                    { value: 16, label: '16px' },
                    { value: 24, label: '24px' },
                  ]}
                />
              </Box>

              {/* Group Horizontal Padding */}
              <Box>
                <Typography gutterBottom>
                  Group Horizontal Padding: {localLayout?.groupHorizontalPadding || 16}px
                </Typography>
                <Slider
                  value={localLayout?.groupHorizontalPadding || 16}
                  onChange={(event, value) => handleLayoutChange('groupHorizontalPadding')(value)}
                  min={0}
                  max={48}
                  step={2}
                  marks={[
                    { value: 0, label: '0px' },
                    { value: 16, label: '16px' },
                    { value: 32, label: '32px' },
                    { value: 48, label: '48px' },
                  ]}
                />
              </Box>

              {/* Group Horizontal Margin */}
              <Box>
                <Typography gutterBottom>
                  Group Horizontal Margin: {localLayout?.groupHorizontalMargin || 8}px
                </Typography>
                <Slider
                  value={localLayout?.groupHorizontalMargin || 8}
                  onChange={(event, value) => handleLayoutChange('groupHorizontalMargin')(value)}
                  min={0}
                  max={32}
                  step={2}
                  marks={[
                    { value: 0, label: '0px' },
                    { value: 8, label: '8px' },
                    { value: 16, label: '16px' },
                    { value: 32, label: '32px' },
                  ]}
                />
              </Box>

              {/* Item Height */}
              <Box>
                <Typography gutterBottom>
                  Tile Item Height: {localLayout?.itemHeight || 120}px
                </Typography>
                <Slider
                  value={localLayout?.itemHeight || 120}
                  onChange={(event, value) => handleLayoutChange('itemHeight')(value)}
                  min={80}
                  max={200}
                  step={10}
                  marks={[
                    { value: 80, label: '80px' },
                    { value: 120, label: '120px' },
                    { value: 160, label: '160px' },
                    { value: 200, label: '200px' },
                  ]}
                />
              </Box>

              {/* Existing Spacing Controls */}
              <Box>
                <Typography gutterBottom>
                  Group Spacing: {localLayout?.groupSpacing || 24}px
                </Typography>
                <Slider
                  value={localLayout?.groupSpacing || 24}
                  onChange={(event, value) => handleLayoutChange('groupSpacing')(value)}
                  min={8}
                  max={48}
                  step={2}
                  marks={[
                    { value: 8, label: '8px' },
                    { value: 24, label: '24px' },
                    { value: 48, label: '48px' },
                  ]}
                />
              </Box>

              <Box>
                <Typography gutterBottom>
                  Tile Spacing: {localLayout?.tileSpacing || 16}px
                </Typography>
                <Slider
                  value={localLayout?.tileSpacing || 16}
                  onChange={(event, value) => handleLayoutChange('tileSpacing')(value)}
                  min={4}
                  max={32}
                  step={2}
                  marks={[
                    { value: 4, label: '4px' },
                    { value: 16, label: '16px' },
                    { value: 32, label: '32px' },
                  ]}
                />
              </Box>

              {/* Layout Save Controls */}
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
                {hasLayoutChanges && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You have unsaved layout changes. Click "Save Layout Changes" to apply them.
                  </Alert>
                )}
                
                {layoutSaveSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Layout changes saved successfully!
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveLayout}
                    disabled={!hasLayoutChanges || layoutSaving}
                    startIcon={<SaveIcon />}
                  >
                    {layoutSaving ? 'Saving...' : 'Save Layout Changes'}
                  </Button>
                  
                  {hasLayoutChanges && (
                    <Button
                      variant="outlined"
                      onClick={() => setLocalLayout(currentLayout ? { ...currentLayout } : null)}
                      disabled={layoutSaving}
                      startIcon={<ResetIcon />}
                    >
                      Reset Changes
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Preview Card */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Preview
                </Typography>
                <Card
                  sx={{
                    backgroundColor: currentTheme.cardBackground,
                    color: currentTheme.textColor,
                    borderRadius: `${currentTheme.borderRadius}px`,
                    border: `1px solid ${currentTheme.primaryColor}20`,
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" sx={{ color: currentTheme.primaryColor }}>
                      Sample Card
                    </Typography>
                    <Typography variant="body2" sx={{ color: currentTheme.textColor }}>
                      This is how your cards will look with the current theme settings.
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        mt: 1,
                        backgroundColor: currentTheme.primaryColor,
                        borderRadius: `${currentTheme.borderRadius}px`,
                      }}
                    >
                      Sample Button
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            </Stack>
          </Box>
        </TabPanel>

        {/* Presets Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ px: 3 }}>
            <ThemePresets onClose={onClose} />
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThemeCustomizer;